import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { saveBlob } from '../../utils/download';
import { errorText } from '../../utils/errorText';
import { badge, box, mono } from '../shared/styles';
import { fmt, significanceLabel } from './significance';
import SynthesisPanel from './SynthesisPanel';

/*
 * One analysis, exactly as it ran: per outcome, the assumption checks, the
 * route they chose, every test with its effect size and CI, and the chart --
 * drawn by the server from the analysis's own snapshot of entered values.
 */

function Chart({ base, outcome, t }) {
  const [kind, setKind] = useState('box');
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    api.get(`${base}/chart`, { params: { outcome, kind } })
      .then(({ data }) => { if (alive) setSvg(data.svg); })
      .catch((e) => { if (alive) setError(errorText(e)); });
    return () => { alive = false; };
  }, [base, outcome, kind]);
  const src = svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : '';
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        {['box', 'bar'].map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}
            title={k === 'bar' ? t('tip.chartBar') : t('tip.chartBox')} data-testid={`chart-${outcome}-${k}`}>{t(`chart_${k}`)}</button>
        ))}
        {svg && (
          <button type="button" onClick={() => saveBlob(new Blob([svg], { type: 'image/svg+xml' }), `${outcome}-${kind}.svg`)}
            title={t('tip.downloadSvg')}>
            {t('downloadSvg')}
          </button>
        )}
      </div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {src && <img src={src} alt={t('chartAlt', { outcome })} data-testid={`chart-${outcome}`}
        style={{ maxWidth: '100%', background: '#fff', borderRadius: 6 }} />}
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('chartNote')}</p>
    </div>
  );
}

function Outcome({ o, base, t }) {
  return (
    <section style={box} data-testid={`outcome-${o.outcome}`}>
      <h4 style={{ marginTop: 0 }}>
        {o.outcome}{o.unit ? ` (${o.unit})` : ''}
        <span style={badge(o.routing === 'refused' ? 'danger' : 'info')} data-testid={`routing-${o.outcome}`}>
          {t(`routing_${o.routing}`)}
        </span>
      </h4>
      {o.refusal && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{t('refused')}: {o.refusal}</p>}
      {o.warnings.map((w) => <p key={w} style={{ color: 'var(--c-warning-fg)', fontSize: 13 }}>{w}</p>)}

      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead><tr>{['group', 'n', 'mean', 'sd', 'median', 'q1', 'q3'].map((c) => <th key={c} style={{ textAlign: 'start' }}>{t(`col_${c}`)}</th>)}</tr></thead>
        <tbody>{o.summaries.map((s) => (
          <tr key={s.group}><td>{s.group}</td><td>{s.n}{s.missing ? ` (+${s.missing})` : ''}</td>
            {['mean', 'sd', 'median', 'q1', 'q3'].map((k) => <td key={k}>{fmt(s[k])}</td>)}</tr>
        ))}</tbody>
      </table>

      {o.assumptions.length > 0 && (
        <>
          <h5>{t('assumptions')}</h5>
          <ul style={{ fontSize: 13 }}>{o.assumptions.map((c, i) => (
            <li key={i}>{t(`check_${c.check}`)} ({c.scope}{c.group ? `: ${c.group}` : ''}) — {t('statistic')} {fmt(c.statistic)}, p {fmt(c.p)} {c.passed ? '✓' : '✗'}</li>
          ))}</ul>
        </>
      )}

      {o.tests.length > 0 && (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }} data-testid={`tests-${o.outcome}`}>
          <thead><tr>{['test', 'comparison', 'statistic', 'df', 'p', 'padj', 'effect', 'sig'].map((c) => (
            <th key={c} style={{ textAlign: 'start' }}>{t(`col_${c}`)}</th>))}</tr></thead>
          <tbody>{o.tests.map((x, i) => (
            <tr key={i}>
              <td>{t(`test_${x.test}`, { defaultValue: x.test })}</td>
              <td>{x.comparison.b ? `${x.comparison.a} – ${x.comparison.b}` : (x.comparison.a || '')}</td>
              <td>{fmt(x.statistic)}</td>
              <td>{[x.df1, x.df2].filter((d) => d !== null && d !== undefined).map(fmt).join(', ')}</td>
              <td>{fmt(x.p)}</td>
              <td>{fmt(x.p_adjusted)}{x.correction ? ` (${x.correction})` : ''}</td>
              <td>{x.effect_size_kind}: {fmt(x.effect_size)} [{fmt(x.ci_low)}, {fmt(x.ci_high)}]</td>
              <td data-testid={`sig-${o.outcome}-${i}`}>{significanceLabel(x.p_adjusted)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {o.tests.length > 0 && <Chart base={base} outcome={o.outcome} t={t} />}
    </section>
  );
}

export default function AnalysisView({ experimentId, analysisId, canSubmit, onReport }) {
  const { t } = useTranslation('experiments');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const base = `/experiments/${experimentId}/analyses/${analysisId}`;

  useEffect(() => {
    let alive = true;
    api.get(base).then(({ data }) => { if (alive) setAnalysis(data); })
      .catch((e) => { if (alive) setError(errorText(e)); });
    return () => { alive = false; };
  }, [base]);

  const exportAs = async (kind, format) => {
    setError('');
    try {
      const { data } = await api.get(`${base}/export`, { params: { kind, format } });
      if (format === 'xlsx') {
        const bytes = Uint8Array.from(atob(data.content_base64), (c) => c.charCodeAt(0));
        saveBlob(new Blob([bytes], { type: data.media_type }), data.filename);
      } else if (format === 'csv') {
        saveBlob(new Blob([data], { type: 'text/csv' }), `${analysisId}-${kind}.csv`);
      } else {
        saveBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `${analysisId}-${kind}.json`);
      }
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (error && !analysis) return <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>;
  if (!analysis) return <p>{t('loading')}</p>;
  const outcomes = analysis.results?.outcomes || [];
  return (
    <div data-testid="analysis">
      <p style={{ fontSize: 13 }}>
        {t('engine')}: <span style={mono}>{analysis.engine_version}</span> · {t('planVersion')} {analysis.plan_version}
        · α = {analysis.results?.alpha ?? '—'} · {t('snapshot')}: <span style={mono}>{analysis.dataset_sha256.slice(0, 12)}…</span>
        · {t('rowsAnalysed', { n: analysis.dataset.rows.length })}
      </p>
      {analysis.status === 'refused' && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{t('refused')}: {analysis.refusal}</p>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {['raw', 'summary', 'results'].flatMap((kind) => ['csv', 'xlsx', 'json'].map((format) => (
          <button key={`${kind}-${format}`} type="button" onClick={() => exportAs(kind, format)}
            title={{ raw: t('tip.exportRaw'), summary: t('tip.exportSummary'), results: t('tip.exportResults') }[kind]}
            data-testid={`export-${kind}-${format}`}>{t(`export_${kind}`)} · {format.toUpperCase()}</button>
        )))}
      </div>
      {outcomes.map((o) => <Outcome key={o.outcome} o={o} base={base} t={t} />)}
      {canSubmit && analysis.status === 'complete' && (
        <SynthesisPanel base={base} onReport={onReport} />
      )}
    </div>
  );
}
